import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { CalendarPlus, Download, Image, Lock, TriangleAlert } from 'lucide-react';
import { adminQueries, getErrorMessage, hasProblemType, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDateTime, formatNumber, hasPermission, METER_EXPORT_KIND_LABELS, METER_READING_STATUS_FILTER_LABELS, parseMeterValue } from '@blimo/shared';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  Input,
  Label,
  PageHeader,
  SearchInput,
  StatCard,
  StatusBadge,
  toast,
} from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { PropertyLink } from '../../components/data.jsx';
import { PropertyFilter } from '../finance/panels.jsx';
import { useListState } from '../../lib/list-search.js';
import { PartyPicker } from './party-picker.jsx';
import { meterLabel, toInputValue, useSupplierOptions, yearFrom, yearOptions } from './utils.js';

const STATUS_OPTIONS = Object.entries(METER_READING_STATUS_FILTER_LABELS).map(([value, label]) => ({ value, label }));

/** Zähler › Zählerstände (Jahr) (F-903…F-906, Exporte F-908): Jahresliste mit Inline-Erfassung, Jahr anlegen/abschließen. */
export function MeterReadingsPage() {
  const api = useApi();
  const { data: me } = useMe();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const year = yearFrom(search.year);
  const params = {
    page: list.apiParams.page,
    per_page: list.apiParams.per_page,
    filter: { status: search.status, property_id: search.property, supplier_id: search.supplier, reader_party_id: search.reader, search: search.search },
  };
  const query = useQuery(adminQueries.meterReadings(api, year, params));
  const supplierOptions = useSupplierOptions();
  const canEdit = hasPermission(me, 'meter-readings.manage');
  const columns = useMemo(() => readingColumns({ canEdit, api }), [canEdit, api]);
  const summary = query.data?.meta?.summary;
  const [openYearOpen, setOpenYearOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const readerName = query.data?.data?.find((r) => String(r.meter?.reader?.id) === search.reader)?.meter?.reader?.name;

  return (
    <>
      <PageHeader
        title="Zählerstände (Jahr)"
        description="Ablesungen eines Jahres mit Vorjahreswert – Werte direkt in der Tabelle erfassen oder korrigieren."
        actions={
          <div className="flex flex-wrap gap-2">
            {hasPermission(me, 'meter-years.open') && (
              <Button size="sm" variant="outline" onClick={() => setOpenYearOpen(true)}>
                <CalendarPlus aria-hidden="true" /> Jahr anlegen
              </Button>
            )}
            {hasPermission(me, 'meter-years.close') && (
              <Button size="sm" variant="outline" onClick={() => setCloseOpen(true)} disabled={!summary}>
                <Lock aria-hidden="true" /> Jahr abschließen
              </Button>
            )}
          </div>
        }
      />
      <FilterBar>
        <FilterSelect label="Jahr" value={String(year)} onChange={(v) => list.setFilter('year', v)} options={yearOptions(year)} allLabel="Aktuelles Jahr" />
        <FilterSelect label="Status" value={search.status} onChange={(v) => list.setFilter('status', v)} options={STATUS_OPTIONS} />
        <PropertyFilter value={search.property} onChange={(v) => list.setFilter('property', v)} />
        <FilterSelect label="Versorger" value={search.supplier} onChange={(v) => list.setFilter('supplier', v)} options={supplierOptions} />
        <PartyPicker size="sm" label="Ableser" value={search.reader} valueLabel={readerName} emptyLabel="Alle" onChange={(v) => list.setFilter('reader', v)} />
        <SearchInput
          label="Zählernummer suchen"
          placeholder="Zählernummer …"
          value={search.search ?? ''}
          onValueChange={(v) => list.setFilter('search', v)}
          wrapperClassName="sm:w-48"
        />
      </FilterBar>

      {summary && (
        <section aria-label="Stand der Ablesung" className="mb-3 grid grid-cols-2 gap-2 lg:grid-cols-4">
          <StatCard label="Offen" value={formatNumber(summary.open)} />
          <StatCard label="Abgelesen" value={formatNumber(summary.read)} />
          <StatCard label="Ohne Ablesung" value={formatNumber(summary.closed)} />
          <StatCard label="Gesamt" value={formatNumber(summary.total)} />
        </section>
      )}

      {hasPermission(me, 'meters.export') && (
        <div role="group" aria-label="Exporte" className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Export {year}:</span>
          {Object.entries(METER_EXPORT_KIND_LABELS).map(([kind, label]) => (
            <Button key={kind} asChild size="sm" variant="outline">
              <a href={api.admin.meterReadings.exportUrl({ year, supplier_id: search.supplier, reader_party_id: search.reader, kind })} download>
                <Download aria-hidden="true" /> {label}
              </a>
            </Button>
          ))}
        </div>
      )}

      {query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <DataTable
          label={`Zählerstände ${year}`}
          columns={columns}
          data={query.data?.data}
          rowCount={query.data?.meta?.total}
          isLoading={query.isPending}
          isFetching={query.isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          emptyTitle="Keine Ablesungen"
          emptyDescription={`Für ${year} gibt es (mit diesen Filtern) keine Ablesungen. Über „Jahr anlegen“ werden die offenen Ablesungen aller aktiven Zähler erzeugt.`}
        />
      )}

      <OpenYearDialog open={openYearOpen} onOpenChange={setOpenYearOpen} defaultYear={year} />
      <CloseYearDialog open={closeOpen} onOpenChange={setCloseOpen} year={year} openCount={summary?.open ?? 0} refetch={query.refetch} />
    </>
  );
}

function readingColumns({ canEdit, api }) {
  return [
    { id: 'property', header: 'Liegenschaft', enableSorting: false, cell: ({ row }) => <PropertyLink property={row.original.meter?.property} /> },
    {
      id: 'meter',
      header: 'Zähler',
      enableSorting: false,
      cell: ({ row }) => {
        const meter = row.original.meter;
        if (!meter) return '–';
        return (
          <Link to="/zaehler/$meterId" params={{ meterId: String(meter.id) }} className="font-medium text-primary hover:underline">
            {meterLabel(meter)}
          </Link>
        );
      },
    },
    { id: 'supplier', header: 'Versorger', enableSorting: false, cell: ({ row }) => row.original.meter?.supplier?.name ?? '–' },
    { id: 'reader', header: 'Ableser', enableSorting: false, cell: ({ row }) => row.original.meter?.reader?.name ?? '–' },
    {
      id: 'previous_value',
      header: 'Vorjahr',
      enableSorting: false,
      meta: { numeric: true },
      cell: ({ row }) => <span className="numeric">{formatNumber(row.original.previous_value, { maximumFractionDigits: 3 })}</span>,
    },
    {
      id: 'value',
      header: 'Wert',
      enableSorting: false,
      meta: { numeric: true },
      cell: ({ row }) =>
        canEdit ? (
          <ValueInput key={`${row.original.id}-${row.original.value}`} reading={row.original} />
        ) : (
          <span className="numeric">{formatNumber(row.original.value, { maximumFractionDigits: 3 })}</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="flex flex-wrap items-center gap-1">
          <StatusBadge domain="meterReading" status={row.original.status} />
          {row.original.is_new_meter && <span className="text-xs text-muted-foreground">neuer Zähler</span>}
        </span>
      ),
    },
    {
      id: 'read_at',
      header: 'Abgelesen',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.read_at ? (
          <span className="flex flex-col text-xs">
            <span className="numeric">{formatDateTime(row.original.read_at)}</span>
            {row.original.read_by?.name && <span className="text-muted-foreground">{row.original.read_by.name}</span>}
          </span>
        ) : (
          <span className="text-muted-foreground">–</span>
        ),
    },
    {
      id: 'photos',
      header: 'Fotos',
      enableSorting: false,
      cell: ({ row }) => {
        const slots = [row.original.has_photo && 1, row.original.has_photo_2 && 2].filter(Boolean);
        if (slots.length === 0) return <span className="text-muted-foreground">–</span>;
        return (
          <span className="flex gap-2">
            {slots.map((slot) => (
              <a
                key={slot}
                href={api.admin.meterReadings.photoUrl(row.original.id, slot)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
                aria-label={`Foto ${slot} zu Zähler ${row.original.meter?.number ?? row.original.id}`}
              >
                <Image className="size-3.5" aria-hidden="true" /> {slot}
              </a>
            ))}
          </span>
        );
      },
    },
  ];
}

/** Inline-Erfassung: speichert bei Enter/Verlassen nur geänderte Werte; leer = wieder offen. */
function ValueInput({ reading }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const initial = toInputValue(reading.value);
  const [text, setText] = useState(initial);
  const [error, setError] = useState(/** @type {string|null} */ (null));
  const [pending, setPending] = useState(false);
  const errorId = `reading-${reading.id}-error`;

  async function commit() {
    if (pending || text.trim() === initial) {
      setError(null);
      return;
    }
    const empty = text.trim() === '';
    const value = empty ? null : parseMeterValue(text);
    if (!empty && value === null) {
      setError('Nur Ziffern und höchstens ein Komma.');
      return;
    }
    setError(null);
    setPending(true);
    try {
      await api.admin.meterReadings.update(reading.id, { value });
      toast.success(empty ? 'Wert entfernt – Ablesung wieder offen' : 'Zählerstand gespeichert');
      await queryClient.invalidateQueries({ queryKey: keys.admin.resource('meter-readings') });
    } catch (e) {
      setError(getErrorMessage(e, 'Der Wert konnte nicht gespeichert werden.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-0.5">
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
          if (e.key === 'Escape') {
            setText(initial);
            setError(null);
          }
        }}
        inputMode="decimal"
        disabled={pending}
        aria-label={`Wert Zähler ${reading.meter?.number ?? reading.id}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className="numeric h-(--control-height-sm) w-28 text-right"
      />
      {error && (
        <span id={errorId} className="text-xs text-destructive">
          {error}
        </span>
      )}
    </div>
  );
}

function OpenYearDialog({ open, onOpenChange, defaultYear }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>{open && <OpenYearBody defaultYear={defaultYear} onClose={() => onOpenChange(false)} />}</DialogContent>
    </Dialog>
  );
}

function OpenYearBody({ defaultYear, onClose }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [year, setYear] = useState(String(defaultYear));
  const [error, setError] = useState(/** @type {string|null} */ (null));
  const [pending, setPending] = useState(false);
  const valid = /^\d{4}$/.test(year) && Number(year) >= 2000 && Number(year) <= 2100;

  async function submit(event) {
    event.preventDefault();
    if (!valid) {
      setError('Bitte ein Jahr zwischen 2000 und 2100 eingeben.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      const result = await api.admin.meterYears.open(Number(year));
      await queryClient.invalidateQueries({ queryKey: keys.admin.resource('meter-readings') });
      toast.success(`${formatNumber(result?.created ?? 0)} Ablesungen für ${year} angelegt`);
      onClose();
    } catch (e) {
      setError(getErrorMessage(e, 'Das Jahr konnte nicht angelegt werden.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>Ablesejahr anlegen</DialogTitle>
        <DialogDescription>Für alle aktiven Zähler ohne Ablesung in diesem Jahr wird eine offene Ablesung (Frist 31.12.) angelegt.</DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-1">
        <Label htmlFor="open-year">Jahr</Label>
        <Input id="open-year" inputMode="numeric" maxLength={4} value={year} onChange={(e) => setYear(e.target.value)} className="w-28" />
      </div>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
          Abbrechen
        </Button>
        <Button type="submit" loading={pending}>
          Anlegen
        </Button>
      </DialogFooter>
    </form>
  );
}

/**
 * Jahresabschluss mit Bestätigung der angezeigten Anzahl offener Werte (`expected_open`). Hat sich die Anzahl
 * inzwischen geändert (409 `stale-confirmation`), wird die Liste neu geladen und erneut gefragt.
 */
function CloseYearDialog({ open, onOpenChange, year, openCount, refetch }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>{open && <CloseYearBody year={year} openCount={openCount} refetch={refetch} onClose={() => onOpenChange(false)} />}</DialogContent>
    </Dialog>
  );
}

function CloseYearBody({ year, openCount, refetch, onClose }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [stale, setStale] = useState(false);
  const [error, setError] = useState(/** @type {string|null} */ (null));
  const [pending, setPending] = useState(false);

  async function confirm() {
    setPending(true);
    setError(null);
    try {
      const result = await api.admin.meterYears.close(year, openCount);
      await queryClient.invalidateQueries({ queryKey: keys.admin.resource('meter-readings') });
      toast.success(`Ablesejahr ${year} abgeschlossen – ${formatNumber(result?.closed ?? 0)} Werte ohne Ablesung`);
      onClose();
    } catch (e) {
      if (hasProblemType(e, 'stale-confirmation')) {
        await refetch();
        setStale(true);
      } else {
        setError(getErrorMessage(e, 'Das Jahr konnte nicht abgeschlossen werden.'));
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Ablesejahr {year} abschließen?</DialogTitle>
        <DialogDescription>
          Alle noch offenen Ablesungen werden als „ohne Ablesung“ abgeschlossen und können im Portal nicht mehr erfasst werden.
        </DialogDescription>
      </DialogHeader>
      <p className="text-sm">
        Offene Werte: <strong className="numeric">{formatNumber(openCount)}</strong>
      </p>
      {stale && (
        <Alert variant="warning" role="alert">
          <TriangleAlert aria-hidden="true" />
          <div className="flex flex-col gap-1">
            <AlertTitle>Anzahl geändert</AlertTitle>
            <AlertDescription>
              Die Anzahl offener Werte hat sich inzwischen geändert und beträgt jetzt {formatNumber(openCount)}. Bitte erneut bestätigen.
            </AlertDescription>
          </div>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
          Abbrechen
        </Button>
        <Button type="button" variant="destructive" loading={pending} onClick={confirm}>
          Abschließen
        </Button>
      </DialogFooter>
    </>
  );
}
